import React from "react";
import {
  Table,
  Segment,
  Button,
  Message,
  Header,
  Grid,
  Input,
  Label,
} from "semantic-ui-react";
import { Form, Formik, Field } from "formik";
import * as Yup from "yup";
import { normalizeId } from "../../../../../formatting/inventoryIds";

const MergeStep = ({
  inventories,
  mergedInventory,
  setMergedInventory,
  supplyUnits,
  onStepComplete,
  onPrevious,
}) => {
  // Create validation schema
  const validationSchema = Yup.object({
    inventory_title: Yup.string().required("Title is required"),
    shelf: Yup.string().required("Shelf is required"),
    inv: Yup.number()
      .required("Inventory value is required")
      .min(0, "Inventory value must be positive"),
  });

  // Handle form submission
  const handleSubmit = (values, { setSubmitting }) => {
    // Update the merged inventory with form values
    setMergedInventory({
      ...mergedInventory,
      inv: values.inv,
      shelf: values.shelf,
      inventory_title: values.inventory_title,
    });

    // Proceed to next step
    onStepComplete();
    setSubmitting(false);
  };

  // Get the total count of all supply units
  const totalSupplyUnits = supplyUnits.length;

  // Custom field component to display errors properly
  const CustomInputField = ({ field, form, label, ...props }) => {
    const hasError = form.touched[field.name] && form.errors[field.name];

    return (
      <div className="field" style={{ marginBottom: "1em" }}>
        <label>{label}</label>
        <Input fluid {...field} {...props} error={Boolean(hasError)} />
        {hasError && (
          <Label basic color="red" pointing>
            {form.errors[field.name]}
          </Label>
        )}
      </div>
    );
  };

  return (
    <Formik
      initialValues={{
        inventory_title: mergedInventory.inventory_title,
        shelf: mergedInventory.shelf,
        inv: mergedInventory.inv,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
    >
      {({ isSubmitting, errors, touched }) => (
        <Form>
          <Segment>
            <Header as="h4">Merge Inventory Records</Header>
            <p>
              Specify the details for the merged inventory record. All supply
              units will be assigned to this single inventory.
            </p>

            {/* Merged Inventory Section */}
            <Segment
              secondary
              style={{ marginBottom: "20px", marginTop: "20px" }}
            >
              <Grid>
                <Grid.Row>
                  <Grid.Column width={8}>
                    <Field
                      name="inventory_title"
                      label="Inventory Title:"
                      placeholder="Enter merged inventory title"
                      component={CustomInputField}
                    />

                    <Field
                      name="shelf"
                      label="Shelf:"
                      placeholder="Enter shelf location"
                      component={CustomInputField}
                    />

                    <Field
                      name="inv"
                      label="Inventory Value:"
                      placeholder="Enter inventory value"
                      type="number"
                      min="0"
                      component={CustomInputField}
                    />
                  </Grid.Column>
                  <Grid.Column width={8}>
                    <Header as="h3">
                      Target Inventory ID:{" "}
                      {normalizeId(mergedInventory.inventory_id)}
                    </Header>
                    <p>Component #: {mergedInventory.part_number}</p>
                    <p>{totalSupplyUnits} total supply units will be merged</p>
                    <Message info>
                      <Message.Header>
                        Merging {inventories.length} Inventory Records
                      </Message.Header>
                      <p>
                        All supply units from the selected inventories will be
                        consolidated into this single inventory record.
                      </p>
                      <p>
                        Inventory record(s){" "}
                        <span style={{ fontWeight: "bold" }}>
                          {inventories
                            .slice(1)
                            .map((inv) => normalizeId(inv.inventory_id))
                            .join(",")}
                        </span>{" "}
                        will be deleted. 🧹
                      </p>
                    </Message>
                  </Grid.Column>
                </Grid.Row>
              </Grid>
            </Segment>

            {/* Supply Units Table */}
            <Header as="h4">Supply Units to be Merged</Header>
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>UPC</Table.HeaderCell>
                  <Table.HeaderCell>Title</Table.HeaderCell>
                  <Table.HeaderCell>Original Inventory ID</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {supplyUnits.map((unit, index) => {
                  // Find the original inventory this unit belonged to
                  const sourceInventory = inventories.find(
                    (inv) => inv.inventory_id === unit.inventory_id
                  );

                  return (
                    <Table.Row key={`${unit.upc}-${index}`}>
                      <Table.Cell>{unit.upc}</Table.Cell>
                      <Table.Cell>{unit.title}</Table.Cell>
                      <Table.Cell>
                        {sourceInventory
                          ? normalizeId(sourceInventory.inventory_id)
                          : "Unknown"}
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>

            {/* Action Buttons */}
            <div
              style={{
                marginTop: "20px",
                display: "flex",
                justifyContent: "space-between",
              }}
            >
              <Button type="button" onClick={onPrevious}>
                Back
              </Button>
              <Button type="submit" primary disabled={isSubmitting}>
                Next
              </Button>
            </div>
          </Segment>
        </Form>
      )}
    </Formik>
  );
};

export default MergeStep;
